import React, {useState, useEffect} from 'react';
import FrontendMappingDialog from './FrontendMappingDialog';

// Feedback overlay component for deployment status
const DeployFeedbackOverlay: React.FC<{
    isVisible: boolean;
    status: string;
    onClose: () => void;
    deploymentError: string | null;
    deploymentMode: string;
}> = ({
          isVisible,
          status,
          onClose,
          deploymentError,
          deploymentMode
      }) => {
    const [progress, setProgress] = useState(0);
    const [statusText, setStatusText] = useState('');
    const [showSuccessAnimation, setShowSuccessAnimation] = useState(false);
    const [showFrontendMappingDialog, setShowFrontendMappingDialog] = useState(false);

    // Reset progress when visibility changes
    useEffect(() => {
        if (isVisible) {
            setProgress(0);
            setShowSuccessAnimation(false);
        }
    }, [isVisible]);

    // Update progress based on status
    useEffect(() => {
        if (!isVisible) return;

        let statusMessage = '';
        let targetProgress = 0;

        if (status === 'compiling') {
            statusMessage = 'Compiling your code...';
            targetProgress = 40;
        } else if (status === 'deploying') {
            statusMessage = 'Deploying to canister...';
            targetProgress = 80;
        } else if (status === 'idle' && progress > 0) {
            // Only show success if we were previously deploying
            statusMessage = 'Deployment completed!';
            targetProgress = 100;

            // Show success animation after deployment completes
            const timer = setTimeout(() => {
                setShowSuccessAnimation(true);
            }, 500);

            return () => clearTimeout(timer);
        }

        setStatusText(statusMessage);

        // Animate progress
        const interval = setInterval(() => {
            setProgress(prev => {
                if (prev < targetProgress) {
                    return prev + 1;
                } else {
                    clearInterval(interval);
                    return prev;
                }
            });
        }, 20);

        return () => clearInterval(interval);
    }, [status, isVisible, progress]);

    // Auto-hide after success - BUT ONLY IF THERE IS NO ERROR
    useEffect(() => {
        // Don't auto-close if there's an error
        if (showSuccessAnimation && !deploymentError) {
            const timer = setTimeout(() => {
                onClose();
            }, 2000);

            return () => clearTimeout(timer);
        }
    }, [showSuccessAnimation, onClose, deploymentError]);

    // Get display name for deployment mode
    const getModeDisplayName = (mode: string) => {
        switch (mode) {
            case 'reinstall':
                return 'Reinstall';
            case 'upgrade':
                return 'Upgrade (EOP)';
            case 'frontend':
                return 'Frontend';
            default:
                return mode;
        }
    };

    if (!isVisible) return null;

    return (
        <div className="deploy-overlay">
            <div className="deploy-modal">
                {deploymentError ? (
                    <div className="deploy-error">
                        <div className="error-icon">❌</div>
                        <h3>Deployment Failed</h3>
                        <div className="deployment-mode-badge error">
                            Mode: {getModeDisplayName(deploymentMode)}
                        </div>
                        <div className="error-message">{deploymentError}</div>
                        <button className="close-button" onClick={onClose}>Close</button>
                    </div>
                ) : (
                    <>
                        {showSuccessAnimation ? (
                            <div className="success-animation">
                                <div className="checkmark-circle">
                                    <div className="checkmark"></div>
                                </div>
                                <h3>Deployment Successful!</h3>
                                <div className="deployment-mode-badge success">
                                    Mode: {getModeDisplayName(deploymentMode)}
                                </div>
                            </div>
                        ) : (
                            <>
                                <div className="spinner-container">
                                    <div className="spinner"></div>
                                </div>
                                <h3>{statusText}</h3>
                                <div className="deployment-mode-badge">
                                    Mode: {getModeDisplayName(deploymentMode)}
                                </div>
                                <div className="progress-container">
                                    <div
                                        className="progress-bar"
                                        style={{width: `${progress}%`}}
                                    ></div>
                                </div>
                                <div className="progress-text">{progress}%</div>
                            </>
                        )}
                    </>
                )}
            </div>
        </div>
    );
};

/**
 * Deployment mode selector component
 */
const InlineDeploymentModeSelector: React.FC<{
    selectedMode: string;
    onModeChange: (mode: string) => void;
    disabled?: boolean;
}> = ({selectedMode, onModeChange, disabled = false}) => {
    // Define available modes with descriptions
    const modes = [
        {
            value: 'reinstall',
            label: 'Reinstall',
            description: 'Completely replaces canister, wiping all existing state'
        },
        {
            value: 'upgrade',
            label: 'Upgrade (EOP)',
            description: 'Preserves all data during code updates with Enhanced Orthogonal Persistence'
        }
    ];

    const [showTooltip, setShowTooltip] = useState(false);
    const [tooltipContent, setTooltipContent] = useState('');

    const handleMouseEnter = (description: string) => {
        setTooltipContent(description);
        setShowTooltip(true);
    };

    const handleMouseLeave = () => {
        setShowTooltip(false);
    };

    return (
        <div style={{position: 'relative', display: 'inline-block', marginRight: '2px', marginBottom: '2px'}}>
            <select
                value={selectedMode}
                onChange={(e) => onModeChange(e.target.value)}
                disabled={disabled}
                style={{
                    backgroundColor: '#775c20',
                    color: '#FFF5EE',
                    padding: '8px 12px',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: disabled ? 'not-allowed' : 'pointer',
                    fontWeight: '500',
                    opacity: disabled ? 0.7 : 1,
                    fontSize: '14px',
                    width: '140px',
                    height: '32px',
                    boxSizing: 'border-box',
                    lineHeight: 'normal',
                    appearance: 'none',
                    WebkitAppearance: 'none',
                    MozAppearance: 'none',
                    backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%23FFFFFF' d='M10.293 3.293L6 7.586 1.707 3.293A1 1 0 00.293 4.707l5 5a1 1 0 001.414 0l5-5a1 1 0 10-1.414-1.414z'/%3E%3C/svg%3E")`,
                    backgroundRepeat: 'no-repeat',
                    backgroundPosition: 'right 8px center',
                    paddingRight: '28px'
                }}
                onMouseEnter={() => handleMouseEnter('Select deployment mode')}
                onMouseLeave={handleMouseLeave}
            >
                {modes.map(mode => (
                    <option
                        key={mode.value}
                        value={mode.value}
                        style={{
                            backgroundColor: '#5C4033',
                            color: '#FFF5EE',
                            fontSize: '14px'
                        }}
                    >
                        {mode.label}
                    </option>
                ))}
            </select>

            {showTooltip && (
                <div style={{
                    position: 'absolute',
                    bottom: '100%',
                    left: '50%',
                    transform: 'translateX(-50%)',
                    marginBottom: '10px',
                    backgroundColor: '#f3e5d8',
                    color: '#5C4033',
                    padding: '8px 12px',
                    borderRadius: '4px',
                    fontSize: '12px',
                    boxShadow: '0 2px 5px rgba(92, 64, 51, 0.2)',
                    width: '200px',
                    textAlign: 'center',
                    zIndex: 1000,
                    whiteSpace: 'normal'
                }}>
                    {tooltipContent}
                    <div style={{
                        position: 'absolute',
                        top: '100%',
                        left: '50%',
                        transform: 'translateX(-50%)',
                        borderWidth: '6px',
                        borderStyle: 'solid',
                        borderColor: '#f3e5d8 transparent transparent transparent'
                    }}></div>
                </div>
            )}
        </div>
    );
};

/**
 * Project type selector component
 */
const ProjectTypeSelector: React.FC<{
    selectedType: string;
    onTypeChange: (type: string) => void;
    disabled?: boolean;
}> = ({selectedType, onTypeChange, disabled = false}) => {
    return (
        <select
            value={selectedType}
            onChange={(e) => onTypeChange(e.target.value)}
            disabled={disabled}
            style={{
                backgroundColor: '#775c20',
                color: '#FFF5EE',
                padding: '8px 12px',
                border: 'none',
                borderRadius: '4px',
                cursor: disabled ? 'not-allowed' : 'pointer',
                fontWeight: '500',
                opacity: disabled ? 0.7 : 1,
                fontSize: '14px',
                width: '160px',
                height: '32px',
                boxSizing: 'border-box',
                lineHeight: 'normal',
                appearance: 'none',
                WebkitAppearance: 'none',
                MozAppearance: 'none',
                backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%23FFFFFF' d='M10.293 3.293L6 7.586 1.707 3.293A1 1 0 00.293 4.707l5 5a1 1 0 001.414 0l5-5a1 1 0 10-1.414-1.414z'/%3E%3C/svg%3E")`,
                backgroundRepeat: 'no-repeat',
                backgroundPosition: 'right 8px center',
                paddingRight: '28px'
            }}
        >
            <option value="react">React (Standard)</option>
            <option value="icpstudio">React (ICP Studio)</option>
            <option value="static">Static</option>
        </select>
    );
};

interface IDEStore {
    setState: (state: any) => void;
}

interface DeployButtonProps {
    onClick: (id: string | null, mode: string, projectType?: string, selectedActor?: string, selectedCanister?: string, selectedActorFile?: string) => void;
    disabled: boolean;
    compilationStatus: string;
    deploymentError: string | null;
    useIDEStore: IDEStore;
    activeType: string;
    files?: any[];
    userCanisters?: any[];
    setActiveFile?: (fileName: string) => void;
    project?: any;
}

/**
 * Enhanced DeployButton component with visual feedback and deployment mode selection
 */
const DeployButton: React.FC<DeployButtonProps> = ({
                                                       onClick,
                                                       disabled,
                                                       compilationStatus,
                                                       deploymentError,
                                                       useIDEStore,
                                                       activeType,
                                                       files = [],
                                                       userCanisters = [],
                                                       setActiveFile,
                                                       project
                                                   }) => {
    // State for deployment mode and feedback visibility
    const [deploymentMode, setDeploymentMode] = useState('reinstall');
    const [projectType, setProjectType] = useState('icpstudio');
    const [availableActors, setAvailableActors] = useState<string[]>([]);
    const [selectedActor, setSelectedActor] = useState<string>('');
    const [selectedCanister, setSelectedCanister] = useState<string>('');
    const [showActorSelection, setShowActorSelection] = useState(false);
    const [showFeedback, setShowFeedback] = useState(false);
    const [showFrontendMappingDialog, setShowFrontendMappingDialog] = useState(false);
    const [pendingFrontendDeploy, setPendingFrontendDeploy] = useState(false);


    // Actor discovery function
    const discoverActors = (files: any[]) => {
        if (!files) return [];

        console.log('🔍 Discovering actors in files:', files.map(f => f.name));

        const actorFiles = files.filter(file => {
            if (!file.name.endsWith('.mo')) return false;

            const content = file.content || '';

            // First, check if it's main.mo or Main.mo (regardless of content)
            if (file.name.endsWith('/main.mo') || file.name.endsWith('/Main.mo')) {
                console.log(`✅ Found main.mo file: ${file.name}`);
                return true;
            }

            // Remove comments first to avoid false positives
            const cleanedContent = content
                .replace(/\/\/.*$/gm, '') // Remove line comments
                .replace(/\/\*[\s\S]*?\*\//g, ''); // Remove block comments

            // More precise regex patterns for actual actor declarations
            const actorPatterns = [
                /^\s*actor\s+\w+\s*(\([^)]*\))?\s*\{/m,      // Named actor at start of line
                /^\s*actor\s+class\s+\w+\s*(\([^)]*\))?\s*\{/m, // Actor class at start of line
                /^\s*actor\s*\{/m,                               // Anonymous actor at start of line
                /^\s*export\s+actor\s+\w+\s*\{/m,                // Exported named actor
                /^\s*export\s+actor\s*\{/m                       // Exported anonymous actor
            ];

            const hasActor = actorPatterns.some(pattern => pattern.test(cleanedContent));

            if (hasActor) {
                console.log(`✅ Found actor in file: ${file.name}`);
            } else {
                console.log(`❌ No actor found in file: ${file.name}`);
            }

            return hasActor;
        });

        const actorFileNames = actorFiles.map(f => f.name);
        console.log('🎯 Final discovered actors:', actorFileNames);

        return actorFileNames;
    };

    // Show feedback when compiling or deploying
    useEffect(() => {
        if (compilationStatus !== 'idle') {
            setShowFeedback(true);
        }
    }, [compilationStatus]);

// Complete useEffect for DeployButton.tsx - Actor discovery and auto-selection

    useEffect(() => {
        if (files) {
            // Always discover actors regardless of activeType (needed for frontend mappings too)
            const actors = discoverActors(files);
            setAvailableActors(actors);

            // Only do auto-selection logic for backend deployments
            if (activeType === 'backend') {
                // 🔧 FIXED: Use project-specific canister filtering
                const projectBackendCanisters = userCanisters?.filter(c =>
                    c.canisterType === 'backend' &&
                    project.canisters.some((projectCanister) =>
                        projectCanister.toText() === c.principal.toText()
                    )
                ) || [];

                // Auto-selection: If single actor + single canister, set them immediately
                if (actors.length === 1 && projectBackendCanisters.length === 1) {
                    const autoActor = actors[0];
                    const autoCanister = projectBackendCanisters[0].principal.toText();

                    console.log(`🎯 DeployButton useEffect AUTO-SELECTION: ${autoActor} + ${autoCanister}`);

                    setSelectedActor(autoActor);
                    setSelectedCanister(autoCanister);

                    // Also pre-store the mapping for consistency
                    const projectName = project?.name || 'unknown-project';
                    const canisterName = getCanisterNameFromActor(autoActor);
                    const storageKey = `${projectName}:${canisterName}`;

                    // Only store if not already stored
                    if (!localStorage.getItem(storageKey)) {
                        localStorage.setItem(storageKey, autoCanister);
                        console.log(`💾 DeployButton pre-stored mapping: ${storageKey} = ${autoCanister}`);
                    }
                } else {
                    // Multiple actors or canisters - just auto-select individually if possible
                    if (actors.length === 1) {
                        setSelectedActor(actors[0]);
                    }

                    if (projectBackendCanisters.length === 1) {
                        setSelectedCanister(projectBackendCanisters[0].principal.toText());
                    }
                }
            }
        }
    }, [files, activeType, userCanisters, project?.name, project?.canisters]);

// 2. FIXED: useEffect for pre-populating dialog
    useEffect(() => {
        if (showActorSelection && availableActors.length > 0) {
            const projectName = project?.name || 'unknown-project';

            // 🔧 FIXED: Use project-specific canister filtering
            const projectBackendCanisters = userCanisters?.filter(c =>
                c.canisterType === 'backend' &&
                project.canisters.some((projectCanister) =>
                    projectCanister.toText() === c.principal.toText()
                )
            ) || [];

            // Try to find a stored mapping for any available actor
            for (const actorFile of availableActors) {
                const canisterName = getCanisterNameFromActor(actorFile);
                const storageKey = `${projectName}:${canisterName}`;
                const storedMapping = localStorage.getItem(storageKey);

                if (storedMapping && storedMapping.trim() !== '') {
                    // Verify the stored canister still exists in project
                    const canisterExists = projectBackendCanisters.some(c => c.principal.toText() === storedMapping);
                    if (canisterExists) {
                        setSelectedActor(actorFile);
                        setSelectedCanister(storedMapping);
                        console.log(`✅ Pre-populated DeployButton dialog: ${actorFile} → ${storedMapping}`);
                        break; // Use the first found mapping
                    }
                }
            }
        }
    }, [showActorSelection, availableActors, userCanisters, project?.name, project?.canisters]);


    // Also show feedback when there's an error
    useEffect(() => {
        if (deploymentError) {
            setShowFeedback(true);
        }
    }, [deploymentError]);

    const handleClick = () => {
        if (activeType === 'backend') {
            // 🔧 FIXED: Use project-specific canister filtering
            const projectBackendCanisters = userCanisters?.filter(c =>
                c.canisterType === 'backend' &&
                project.canisters.some((projectCanister) =>
                    projectCanister.toText() === c.principal.toText()
                )
            ) || [];

            console.log(`🎯 Deploy clicked - Found ${availableActors.length} actors and ${projectBackendCanisters.length} project backend canisters`);

            // 🔧 FIXED: Auto-deploy when single actor + single project canister
            if (availableActors.length === 1 && projectBackendCanisters.length === 1) {
                const autoSelectedActor = availableActors[0];
                const autoSelectedCanister = projectBackendCanisters[0].principal.toText();

                console.log(`✅ AUTO-MAPPING: ${autoSelectedActor} → ${autoSelectedCanister}`);

                // Store the mapping in localStorage
                const projectName = project?.name || 'unknown-project';
                const canisterName = getCanisterNameFromActor(autoSelectedActor);
                const storageKey = `${projectName}:${canisterName}`;

                localStorage.setItem(storageKey, autoSelectedCanister);
                console.log(`💾 Auto-stored mapping: ${storageKey} = ${autoSelectedCanister}`);

                // Switch to the actor file before deploying
                if (setActiveFile) {
                    setActiveFile(autoSelectedActor);
                    console.log(`📁 Auto-switched active file to: ${autoSelectedActor}`);
                }

                // Deploy immediately without showing any dialog
                setShowFeedback(true);
                onClick(null, deploymentMode, undefined, autoSelectedActor, autoSelectedCanister, autoSelectedActor);
                return;
            }

            // 🔧 FIXED: Only show dialog when multiple actors OR multiple project canisters
            if (availableActors.length > 1 || projectBackendCanisters.length > 1) {
                console.log(`🔄 Multiple options found - showing selection dialog`);
                setShowActorSelection(true);
                return;
            }

            // Handle edge cases
            if (availableActors.length === 0) {
                console.error('❌ No actors found in project files');
                alert('No actor files found in the project. Please create an actor file first.');
                return;
            }

            if (projectBackendCanisters.length === 0) {
                console.error('❌ No project backend canisters found');
                alert('No backend canisters found for this project. Please create a backend canister first.');
                return;
            }

            // Final fallback - use selected values or first available
            const actualActor = selectedActor || availableActors[0];
            const actualCanister = selectedCanister || projectBackendCanisters[0]?.principal.toText();

            if (actualActor && actualCanister) {
                console.log(`🎯 Fallback deployment: ${actualActor} → ${actualCanister}`);

                // Store mapping
                const projectName = project?.name || 'unknown-project';
                const canisterName = getCanisterNameFromActor(actualActor);
                localStorage.setItem(`${projectName}:${canisterName}`, actualCanister);

                if (setActiveFile) {
                    setActiveFile(actualActor);
                }

                setShowFeedback(true);
                onClick(null, deploymentMode, undefined, actualActor, actualCanister, actualActor);
            }
        } else {
            // Frontend deployment logic - updated for single actor auto-deploy
            if (availableActors.length <= 1) {
                // Single or no actors - check if we can auto-deploy
                if (availableActors.length === 1) {
                    // 🔧 FIXED: Use project-specific canister filtering for auto-mapping
                    const projectBackendCanisters = userCanisters?.filter(c =>
                        c.canisterType === 'backend' &&
                        project.canisters.some((projectCanister) =>
                            projectCanister.toText() === c.principal.toText()
                        )
                    ) || [];

                    if (projectBackendCanisters.length === 1) {
                        // Auto-store mapping for single actor + single project canister
                        const projectName = project?.name || 'unknown-project';
                        const canisterName = getCanisterNameFromActor(availableActors[0]);
                        const storageKey = `${projectName}:${canisterName}`;

                        try {
                            localStorage.setItem(storageKey, projectBackendCanisters[0].principal.toText());
                            console.log(`💾 Frontend auto-stored mapping: ${storageKey} = ${projectBackendCanisters[0].principal.toText()}`);
                        } catch (error) {
                            console.error('❌ Frontend auto-mapping storage failed:', error);
                        }
                    }
                }

                // Auto-deploy frontend
                setShowFeedback(true);
                onClick(null, 'frontend', projectType);
            } else {
                // Multiple actors - check if all mappings exist
                if (checkFrontendMappings()) {
                    setShowFeedback(true);
                    onClick(null, 'frontend', projectType);
                } else {
                    setShowFrontendMappingDialog(true);
                }
            }
        }
    };


    const checkFrontendMappings = (): boolean => {
        if (availableActors.length === 0) return true;

        const projectName = project?.name || 'unknown-project';

        for (const actorFile of availableActors) {
            const canisterName = getCanisterNameFromActor(actorFile);
            const storageKey = `${projectName}:${canisterName}`;
            const mapping = localStorage.getItem(storageKey);

            if (!mapping || mapping.trim() === '') {
                return false;
            }
        }
        return true;
    };

    const handleFrontendMappingComplete = (mappings: Record<string, string>) => {
        setShowFrontendMappingDialog(false);

        // Switch to frontend type before deploying
        useIDEStore.setState({ activeType: 'frontend' });

        setShowFeedback(true);
        onClick(null, 'frontend', projectType);
    };



    const getCanisterNameFromActor = (actorFile: string): string => {
        // Extract name from path like "MyProject/src/backend/src/main.mo"
        const fileName = actorFile.split('/').pop() || actorFile;

        // Remove extension and normalize
        const baseName = fileName.replace('.mo', '');

        // Convert to canister naming convention
        if (baseName.toLowerCase() === 'main') return 'main';
        return baseName.toLowerCase();
    };





    // Handle closing the feedback
    const handleClose = () => {
        setShowFeedback(false);
        // if (deploymentError) {
        //     // Use the Zustand store to clear the error
        //     useIDEStore.setState({deploymentError: null});
        // }
    };

    // Handle deployment mode change
    const handleModeChange = (mode: string) => {
        setDeploymentMode(mode);
        // Save this preference in the IDE store
        useIDEStore.setState({preferredDeploymentMode: mode});
    };

    // Handle project type change
    const handleProjectTypeChange = (type: string) => {
        setProjectType(type);
        // Save this preference in the IDE store
        useIDEStore.setState({preferredProjectType: type});
    };

    // Determine button text based on status
    let buttonText = 'Deploy';
    if (compilationStatus === 'compiling') buttonText = 'Compiling...';
    if (compilationStatus === 'deploying') buttonText = 'Deploying...';

    // Determine if we're in a busy state
    const isBusy = compilationStatus !== 'idle';


    return (
        <>
            <div style={{display: 'flex', alignItems: 'center', gap: '10px'}}>
                {/* Deploy Button */}
                <button
                    onClick={handleClick}
                    disabled={disabled || isBusy}
                    style={{
                        backgroundColor: isBusy ? "#9B5523" : "#5C4033",
                        color: "#FFF5EE",
                        padding: "8px 16px",
                        border: "none",
                        borderRadius: "4px",
                        cursor: (disabled || isBusy) ? "not-allowed" : "pointer",
                        opacity: (disabled || isBusy) ? 0.7 : 1,
                        fontWeight: "500",
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                        height: "32px"
                    }}
                >
                    {isBusy && (
                        <span className="button-spinner"></span>
                    )}
                    {buttonText}
                </button>

                {/* Show mode selector for backend */}
                {activeType === 'backend' && (
                    <InlineDeploymentModeSelector
                        selectedMode={deploymentMode}
                        onModeChange={handleModeChange}
                        disabled={disabled || isBusy}
                    />
                )}

                {/* Show project type selector for frontend */}
                {activeType === 'frontend' && (
                    <ProjectTypeSelector
                        selectedType={projectType}
                        onTypeChange={handleProjectTypeChange}
                        disabled={disabled || isBusy}
                    />
                )}

                {/* Actor Selection Dialog */}
                {showActorSelection && (
                    <div style={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        backgroundColor: 'rgba(0, 0, 0, 0.5)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        zIndex: 2000
                    }}>
                        <div style={{
                            backgroundColor: '#fdf6e3',
                            borderRadius: '8px',
                            padding: '24px',
                            width: '450px',
                            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)'
                        }}>
                            <h3 style={{
                                margin: '0 0 16px 0',
                                color: '#5C4033',
                                fontSize: '18px',
                                fontWeight: '600',
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis'
                            }}>
                                🎯 Select Backend Actor & Canister
                            </h3>
                            <p style={{color: '#8B4513', fontSize: '14px', marginBottom: '20px'}}>
                                Multiple backend actors or canisters found. Please select the actor and target canister for deployment.
                            </p>

                            <div style={{marginBottom: '16px'}}>
                                <label style={{
                                    display: 'block',
                                    marginBottom: '8px',
                                    color: '#5C4033',
                                    fontWeight: '500'
                                }}>
                                    Actor to Deploy:
                                </label>
                                <select
                                    value={selectedActor}
                                    onChange={(e) => setSelectedActor(e.target.value)}
                                    style={{
                                        width: '100%',
                                        padding: '10px',
                                        borderRadius: '4px',
                                        border: '1px solid #8B4513',
                                        backgroundColor: '#fff',
                                        fontSize: '14px'
                                    }}
                                >
                                    <option value="">Select an actor...</option>
                                    {availableActors.map(actor => (
                                        <option key={actor} value={actor}>{actor}</option>
                                    ))}
                                </select>
                            </div>

                            <div style={{marginBottom: '24px'}}>
                                <label style={{
                                    display: 'block',
                                    marginBottom: '8px',
                                    color: '#5C4033',
                                    fontWeight: '500'
                                }}>
                                    Target Canister:
                                </label>
                                <select
                                    value={selectedCanister}
                                    onChange={(e) => setSelectedCanister(e.target.value)}
                                    style={{
                                        width: '100%',
                                        padding: '10px',
                                        borderRadius: '4px',
                                        border: '1px solid #8B4513',
                                        backgroundColor: '#fff',
                                        fontSize: '14px'
                                    }}
                                >
                                    <option value="">Select a canister...</option>
                                    {/* 🔧 FIXED: Use project-specific filtering */}
                                    {userCanisters?.filter(c =>
                                        c.canisterType === 'backend' &&
                                        project.canisters.some((projectCanister) =>
                                            projectCanister.toText() === c.principal.toText()
                                        )
                                    ).map(canister => (
                                        <option key={canister.principal.toText()} value={canister.principal.toText()}>
                                            {canister.name || canister.principal.toText()}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            <div style={{display: 'flex', gap: '12px', justifyContent: 'flex-end'}}>
                                <button
                                    onClick={() => setShowActorSelection(false)}
                                    style={{
                                        padding: '10px 20px',
                                        backgroundColor: '#8B4513',
                                        color: '#FFF5EE',
                                        border: 'none',
                                        borderRadius: '4px',
                                        cursor: 'pointer',
                                        fontWeight: '500'
                                    }}
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={() => {
                                        if (selectedActor && selectedCanister) {
                                            setShowActorSelection(false);

                                            // Store canister mapping in localStorage
                                            const projectName = project?.name || 'unknown-project';
                                            const canisterName = getCanisterNameFromActor(selectedActor);
                                            localStorage.setItem(`${projectName}:${canisterName}`, selectedCanister);
                                            console.log(`💾 Stored mapping: ${projectName}:${canisterName} = ${selectedCanister}`);

                                            // Switch to the actor file before deploying
                                            if (setActiveFile) {
                                                setActiveFile(selectedActor);
                                                console.log(`📁 Switched active file to: ${selectedActor}`);
                                            }

                                            setShowFeedback(true);
                                            onClick(null, deploymentMode, undefined, selectedActor, selectedCanister, selectedActor);
                                        }
                                    }}
                                    disabled={!selectedActor || !selectedCanister}
                                    style={{
                                        padding: '10px 20px',
                                        backgroundColor: selectedActor && selectedCanister ? '#5C4033' : '#ccc',
                                        color: '#FFF5EE',
                                        border: 'none',
                                        borderRadius: '4px',
                                        cursor: selectedActor && selectedCanister ? 'pointer' : 'not-allowed',
                                        fontWeight: '500'
                                    }}
                                >
                                    🟣 Deploy Backend
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>

            {showFrontendMappingDialog && (
                <FrontendMappingDialog
                    isOpen={showFrontendMappingDialog}
                    onClose={() => setShowFrontendMappingDialog(false)}
                    onProceedWithMappings={handleFrontendMappingComplete}
                    availableActors={availableActors}
                    userCanisters={userCanisters}
                    project={project}
                    getCanisterNameFromActor={getCanisterNameFromActor}
                />
            )}

            <DeployFeedbackOverlay
                isVisible={showFeedback}
                status={compilationStatus}
                onClose={handleClose}
                deploymentError={deploymentError}
                deploymentMode={activeType === 'frontend' ? 'frontend' : deploymentMode}
            />

            <style>{`
        /* Button spinner */
        .button-spinner {
          width: 16px;
          height: 16px;
          border: 2px solid rgba(255, 255, 255, 0.3);
          border-radius: 50%;
          border-top-color: #fff;
          animation: spin 1s linear infinite;
        }
        
        /* Deploy overlay */
        .deploy-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background-color: rgba(0, 0, 0, 0.5);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 1000;
          backdrop-filter: blur(3px);
        }
        
        .deploy-modal {
          background-color: #fdf6e3;
          border-radius: 8px;
          box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
          padding: 24px;
          width: 400px;
          text-align: center;
          display: flex;
          flex-direction: column;
          align-items: center;
        }
        
        .spinner-container {
          margin-bottom: 16px;
        }
        
        .spinner {
          width: 50px;
          height: 50px;
          border: 4px solid rgba(92, 64, 51, 0.2);
          border-radius: 50%;
          border-top-color: #5C4033;
          animation: spin 1s linear infinite;
        }
        
        @keyframes spin {
          to {
            transform: rotate(360deg);
          }
        }
        
        .deploy-modal h3 {
          margin: 0 0 16px 0;
          color: #5C4033;
          font-size: 18px;
        }
        
        .progress-container {
          width: 100%;
          height: 8px;
          background-color: #e2d8c7;
          border-radius: 4px;
          overflow: hidden;
          margin-bottom: 8px;
        }
        
        .progress-bar {
          height: 100%;
          background-color: #8B4513;
          border-radius: 4px;
          transition: width 0.3s ease;
        }
        
        .progress-text {
          color: #5C4033;
          font-size: 14px;
          font-weight: 500;
        }
        
        /* Success animation */
        .success-animation {
          display: flex;
          flex-direction: column;
          align-items: center;
          padding: 16px 0;
        }
        
        .checkmark-circle {
          width: 56px;
          height: 56px;
          position: relative;
          background-color: #4caf50;
          border-radius: 50%;
          margin-bottom: 16px;
          display: flex;
          align-items: center;
          justify-content: center;
          animation: scaleIn 0.3s ease-out;
        }
        
        .checkmark {
          width: 30px;
          height: 15px;
          border-bottom: 4px solid white;
          border-left: 4px solid white;
          transform: rotate(-45deg) translate(3px, -3px);
          animation: drawCheck 0.5s ease-out 0.2s forwards;
          opacity: 0;
        }
        
        @keyframes scaleIn {
          from {
            transform: scale(0);
          }
          to {
            transform: scale(1);
          }
        }
        
        @keyframes drawCheck {
          from {
            width: 0;
            height: 0;
            opacity: 0;
          }
          to {
            width: 30px;
            height: 15px;
            opacity: 1;
          }
        }
        
        /* Error styles */
        .deploy-error {
          display: flex;
          flex-direction: column;
          align-items: center;
          width: 100%;
        }
        
        .error-icon {
          font-size: 40px;
          margin-bottom: 16px;
          animation: shake 0.5s ease-in-out;
        }
        
        .error-message {
          background-color: #ffedeb;
          border-radius: 6px;
          padding: 12px;
          margin-bottom: 16px;
          color: #d32f2f;
          text-align: left;
          font-size: 14px;
          max-height: 200px;
          overflow-y: auto;
          width: 100%;
          border-left: 4px solid #d32f2f;
        }
        
        .close-button {
          background-color: #5C4033;
          color: white;
          border: none;
          padding: 8px 16px;
          border-radius: 4px;
          cursor: pointer;
          font-weight: 500;
        }
        
        @keyframes shake {
          0%, 100% { transform: translateX(0); }
          10%, 30%, 50%, 70%, 90% { transform: translateX(-6px); }
          20%, 40%, 60%, 80% { transform: translateX(6px); }
        }
        
        /* Deployment mode badge */
        .deployment-mode-badge {
          background-color: #e6d9c4;
          color: #5C4033;
          padding: 4px 10px;
          border-radius: 12px;
          font-size: 12px;
          font-weight: 500;
          margin-bottom: 16px;
          display: inline-block;
        }
        
        .deployment-mode-badge.success {
          background-color: #e8f5e9;
          color: #2e7d32;
        }
        
        .deployment-mode-badge.error {
          background-color: #ffedeb;
          color: #d32f2f;
          margin-bottom: 12px;
        }
      `}</style>
        </>
    );
};

export default DeployButton;